import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Building2,
    CalendarRange,
    AlertOctagon,
    FileBarChart2,
    LogOut,
    ChevronDown,
    Lock,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import ZentraLogo from "@/components/ZentraLogo";

const PORTAL_NAV = [
    { to: "/portal", label: "Resumen", icon: LayoutDashboard, end: true },
    { to: "/portal/propiedades", label: "Mis propiedades", icon: Building2 },
    { to: "/portal/calendario", label: "Calendario", icon: CalendarRange },
    { to: "/portal/incidencias", label: "Incidencias", icon: AlertOctagon },
    { to: "/portal/reportes", label: "Informes", icon: FileBarChart2 },
];

export default function ClientLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const currentTitle =
        PORTAL_NAV.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label ||
        "Portal cliente";

    return (
        <div className="flex min-h-screen bg-zentra-bg" data-testid="client-portal-shell">
            <aside
                className="hidden lg:flex w-64 flex-col text-zentra-bg sticky top-0 h-screen"
                style={{ background: "#0d2419" }}
                data-testid="portal-sidebar"
            >
                <div className="px-6 pt-7 pb-6 border-b border-white/5">
                    <ZentraLogo variant="light" layout="horizontal" size="md" />
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-zentra-accent px-2 py-1 rounded-md border border-zentra-accent/30 bg-zentra-accent/10">
                        <Lock size={10} /> Portal cliente
                    </div>
                </div>
                <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" data-testid="portal-nav">
                    <p className="px-3 text-[10px] font-semibold tracking-widest text-white/40 uppercase mb-2">
                        Vista cliente
                    </p>
                    {PORTAL_NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) => `zentra-nav-link ${isActive ? "is-active" : ""}`}
                            data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <item.icon size={17} strokeWidth={1.7} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="px-4 py-4 border-t border-white/5">
                    <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Acceso restringido</div>
                    <p className="text-xs text-white/60 leading-relaxed">
                        Vista solo de lectura sobre tus propiedades en gestión.
                    </p>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="bg-white border-b border-zentra-border sticky top-0 z-30" data-testid="portal-topbar">
                    <div className="flex items-center justify-between px-6 py-3 gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="lg:hidden">
                                <ZentraLogo variant="dark" layout="horizontal" size="sm" />
                            </div>
                            <div className="hidden lg:flex flex-col leading-tight">
                                <span className="text-[11px] tracking-widest uppercase text-zentra-muted">
                                    Portal cliente
                                </span>
                                <h1 className="font-display text-lg font-semibold text-zentra-ink">
                                    {currentTitle}
                                </h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen((v) => !v)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zentra-border bg-white hover:border-zentra-olive transition"
                                    data-testid="portal-user-button"
                                >
                                    <div className="w-7 h-7 rounded-md bg-zentra-primary text-white font-display font-semibold flex items-center justify-center text-xs">
                                        {(user?.name || "C").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="hidden md:flex flex-col leading-tight items-start">
                                        <span className="text-xs font-semibold text-zentra-ink truncate max-w-[140px]">
                                            {user?.name}
                                        </span>
                                        <span className="text-[10px] text-zentra-muted tracking-wider uppercase">
                                            Cliente
                                        </span>
                                    </div>
                                    <ChevronDown size={14} className="text-zentra-muted" />
                                </button>
                                {menuOpen && (
                                    <div
                                        className="absolute right-0 top-12 w-56 bg-white border border-zentra-border rounded-lg shadow-lg p-2 z-50"
                                        data-testid="portal-user-menu"
                                    >
                                        <div className="px-3 py-2 border-b border-zentra-border mb-1">
                                            <p className="text-sm font-semibold text-zentra-ink">{user?.name}</p>
                                            <p className="text-xs text-zentra-muted">{user?.email}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zentra-ink hover:bg-zentra-bg transition"
                                            data-testid="portal-logout-button"
                                        >
                                            <LogOut size={14} />
                                            Cerrar sesión
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <nav
                        className="lg:hidden flex items-center gap-1 px-2 pb-2 overflow-x-auto"
                        data-testid="portal-mobile-nav"
                    >
                        {PORTAL_NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition ${
                                        isActive
                                            ? "bg-zentra-primary text-white"
                                            : "text-zentra-ink bg-zentra-bg hover:bg-zentra-border/50"
                                    }`
                                }
                            >
                                <item.icon size={14} />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </header>

                <main className="flex-1 p-5 md:p-8 max-w-[1400px] w-full mx-auto" data-testid="portal-main">
                    {children}
                </main>
            </div>
        </div>
    );
}
